import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { getHizmetlerPage, STRAPI_URL } from '../services/api';
import ScrollReveal from '../components/ScrollReveal';
import Seo from '../components/Seo';
import DEFAULT_DATA from '../default_datas/pages/defaultHizmetler';


const Hizmetler = () => {
  const navigate = useNavigate();
  const { lang } = useLang();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  const tx = (tr, en) => (lang === 'en' ? (en || tr) : tr);

  useEffect(() => {
    getHizmetlerPage()
      .then((res) => {
        if (res) setPageData(res);
      })
      .catch((err) => {
        console.error('Hizmetler sayfası veri çekme hatası:', err);
      })
      .finally(() => setLoading(false));
  }, []);

  const Hero = ({ data }) => {
    const d = { ...DEFAULT_DATA.hero, ...data };
    const imageUrl = d.image?.url
      ? d.image.url.startsWith('http')
        ? d.image.url
        : `${STRAPI_URL}${d.image.url}`
      : null;

    return (
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 py-10 lg:py-20 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div className="flex flex-col gap-4 lg:gap-6 text-center lg:text-left items-center lg:items-start">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 lg:px-4 lg:py-1.5 rounded-full bg-[rgba(15,189,73,0.1)] text-[#2a9d8f] text-[10px] lg:text-xs font-bold tracking-[1.5px] lg:tracking-[2px] uppercase w-fit">
            <span className="material-symbols-outlined text-sm lg:text-base">verified</span>
            {tx(d.badge, d.badge_en)}
          </div>

          <h1 className="text-3xl lg:text-6xl font-black leading-tight tracking-tight text-[#0d1f17] m-0">
            {tx(d.title, d.title_en)}
          </h1>

          <p className="text-sm lg:text-lg leading-relaxed text-[#4a5568] m-0 max-w-xl">
            {tx(d.description, d.description_en)}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mt-2 w-full lg:w-auto">
            <button
              onClick={() => navigate('/iletisim')}
              className="px-6 lg:px-8 py-3.5 lg:py-4 font-bold rounded-xl bg-[#2a9d8f] text-white border-none cursor-pointer hover:bg-[#0da641] transition-all shadow-lg shadow-green-100 text-sm lg:text-base"
            >
              {tx(d.primaryButtonText, d.primaryButtonText_en)}
            </button>

            <button
              onClick={() => navigate('/iletisim')}
              className="px-6 lg:px-8 py-3.5 lg:py-4 font-bold rounded-xl bg-white text-[#2a9d8f] border border-[#e2e8f0] cursor-pointer hover:bg-gray-50 transition-all text-sm lg:text-base"
            >
              {tx(d.secondaryButtonText, d.secondaryButtonText_en)}
            </button>
          </div>
        </div>

        <div className="relative mt-8 lg:mt-0">
          <div className="relative z-10 p-2 lg:p-4 bg-[#e8f5f2] rounded-[24px] lg:rounded-[32px]">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={tx(d.title, d.title_en)}
                className="rounded-xl lg:rounded-2xl shadow-2xl w-full h-[250px] lg:h-auto object-cover border border-white"
              />
            ) : (
              <div className="w-full h-[250px] lg:h-[400px] bg-slate-200 rounded-2xl flex items-center justify-center text-gray-400 text-sm">
                {lang === 'en' ? 'Image Not Loaded' : 'Görsel Yüklenmedi'}
              </div>
            )}
          </div>

          {/* Compliance Badge - Mobilde sağ alta kompakt bir şekilde */}
          <div className="absolute -bottom-6 -right-2 lg:-bottom-10 lg:-left-12 z-30 bg-white/95 backdrop-blur-sm p-3 lg:p-5 rounded-[16px] lg:rounded-[24px] shadow-xl border border-white/20 flex flex-col gap-0.5 min-w-[140px] lg:min-w-[210px]">
            <span className="text-[8px] lg:text-[10px] text-[#94a3b8] font-extrabold uppercase tracking-[1px] lg:tracking-[1.5px]">
              {lang === 'en' ? 'Automated Rate' : 'Uyum Oranı'}
            </span>

            <div className="flex items-center justify-between gap-2 lg:gap-4">
              <div className="flex items-baseline gap-0.5 lg:gap-1">
                <span className="text-2xl lg:text-[38px] font-black text-[#2a9d8f] leading-none">
                  %{d.complianceRate}
                </span>
                {d.complianceTrend && (
                  <span className="text-[8px] lg:text-[10px] font-bold text-[#2a9d8f]">
                    +{d.complianceTrend}%
                  </span>
                )}
              </div>
              <div className="flex items-center justify-center w-7 h-7 lg:w-10 lg:h-10 rounded-lg lg:rounded-2xl bg-[#e8f5f2]">
                <span className="material-symbols-outlined text-[#2a9d8f] text-base lg:text-[22px] font-bold">
                  trending_up
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  };

  const Stats = ({ data }) => {
    const d = { ...DEFAULT_DATA.stats, ...data };

    return (
      <section className="bg-white py-8 lg:py-10 border-t border-b border-[#f1f5f9]">
        <div className="max-w-[1100px] mx-auto px-6 grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
          {d.stats?.map((s, i) => (
            <div
              key={i}
              className="bg-[#f8fafc] rounded-xl lg:rounded-2xl px-4 py-4 lg:px-6 lg:py-5 flex flex-col gap-1 border border-gray-50 hover:shadow-sm transition-shadow"
            >
              <p className="text-[8px] lg:text-[10px] font-bold tracking-[1px] lg:tracking-[1.2px] uppercase text-[#94a3b8] m-0">
                {tx(s.title || s.label, s.title_en || s.label_en)}
              </p>

              <div className="flex items-baseline justify-between mt-0.5">
                <h3 className="text-xl lg:text-[32px] font-bold m-0 leading-none text-[#15614F]">
                  {tx(s.value, s.value_en) || s.value}
                </h3>
                <span className="material-symbols-outlined text-base lg:text-[20px] text-[#cbd5e1]">
                  {s.icon}
                </span>
              </div>

              <p className="text-[9px] lg:text-[11px] text-[#94a3b8] m-0 font-medium opacity-80 leading-tight">
                {tx(s.subText, s.subText_en)}
              </p>
            </div>
          ))}
        </div>
      </section>
    );
  };

  const Features = ({ data }) => {
    const d = { ...DEFAULT_DATA.features, ...data };

    return (
      <section className="py-12 lg:py-24 bg-[#f0f4f0]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="max-w-xl mb-10 lg:mb-14 text-center lg:text-left mx-auto lg:mx-0">
            <h2 className="text-2xl lg:text-4xl font-black text-[#0d1f17] mb-3 lg:mb-4 leading-tight">
              {tx(d.Title, d.Title_en)}
            </h2>
            <p className="text-sm lg:text-lg text-[#4a5568] leading-relaxed m-0">
              {tx(d.description, d.Description_en)}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {d.cards?.map((f, i) => (
              <div
                key={i}
                className="bg-white border border-[#e2e8f0] rounded-2xl p-6 lg:p-9 flex flex-col gap-4 shadow-sm hover:shadow-xl transition-all"
              >
                <div className="w-10 h-10 lg:w-14 lg:h-14 rounded-xl bg-[#e8f5f2] text-[#4BA18C] flex items-center justify-center">
                  <span className="material-symbols-outlined text-2xl lg:text-3xl">{f.icon}</span>
                </div>

                <h3 className="text-base lg:text-lg font-black text-[#0d1f17] m-0">
                  {tx(f.title, f.title_en)}
                </h3>

                <p className="text-xs lg:text-sm text-[#4a5568] leading-relaxed m-0">
                  {tx(f.description, f.description_en)}
                </p>

                <ul className="list-none p-0 m-0 flex flex-col gap-2 border-t border-gray-50 pt-4">
                  {f.bullets?.map((bullet, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-[11px] lg:text-sm text-[#4a5568]">
                      <span className="material-symbols-outlined text-sm lg:text-base text-[#4BA18C] shrink-0">
                        check_circle
                      </span>
                      <span className="font-medium">{tx(bullet.title || bullet.Title, bullet.title_en || bullet.Title_en)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  const CTA = ({ data }) => {
    const d = { ...DEFAULT_DATA.cta, ...data };

    return (
      <section className="bg-white py-10 lg:py-24 px-6">
        <div className="max-w-[1280px] mx-auto bg-[#e8f5f2] rounded-2xl lg:rounded-[3rem] p-8 lg:p-24 text-center flex flex-col items-center gap-4 lg:gap-8">
          <h2 className="text-2xl lg:text-4xl font-black text-[#0d1f17] m-0 leading-tight">
            {tx(d.Title || d.title, d.Title_en || d.title_en)}
          </h2>

          <p className="text-sm lg:text-lg text-[#4a5568] max-w-xl mx-auto m-0 leading-relaxed">
            {tx(d.Description || d.description, d.Description_en || d.description_en)}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={() => navigate('/iletisim')}
              className="px-6 lg:px-8 py-3 lg:py-4 font-bold rounded-xl bg-[#4BA18C] text-white border-none cursor-pointer text-sm lg:text-base shadow-lg"
            >
              {tx(d.primaryButtonText, d.primaryButtonText_en)}
            </button>

            <button
              onClick={() => navigate('/iletisim')}
              className="px-6 lg:px-8 py-3 lg:py-4 font-bold rounded-xl bg-white text-[#0d1f17] border border-[#e2e8f0] cursor-pointer text-sm lg:text-base"
            >
              {tx(d.secondaryButtonText, d.secondaryButtonText_en)}
            </button>
          </div>
        </div>
      </section>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-[#2a9d8f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!pageData || !pageData.pageContent?.length) {
    return (
      <div className="min-h-screen font-poppins">
        <Seo
          title={lang === 'en' ? 'Service Solutions | RUZY' : 'Hizmet Çözümleri | RUZY'}
          path="/hizmetler"
        />
        <ScrollReveal><Hero data={{}} /></ScrollReveal>
        <ScrollReveal><Stats data={{}} /></ScrollReveal>
        <ScrollReveal><Features data={{}} /></ScrollReveal>
        <ScrollReveal><CTA data={{}} /></ScrollReveal>
      </div>
    );
  }

  return (
    <div className="min-h-screen font-poppins overflow-x-hidden">
      <Seo
        title={lang === 'en' ? 'Service Solutions | RUZY' : 'Hizmet Çözümleri | RUZY'}
        path="/hizmetler"
      />

      {pageData.pageContent?.map((section, index) => (
        <ScrollReveal key={index}>
          {section.__component === 'sections.hero' && <Hero data={section} />}
          {section.__component === 'sections.stats-section' && <Stats data={section} />}
          {section.__component === 'sections.features-section' && <Features data={section} />}
          {section.__component === 'sections.cta' && <CTA data={section} />}
        </ScrollReveal>
      ))}
    </div>
  );
};

export default Hizmetler;